import Lenis from 'lenis';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SmoothScroll from '../singleton/SmoothScroll';
import WindowSize from '../singleton/WindowSize';

gsap.registerPlugin(ScrollTrigger);

class Header {
  private desktopBreakpoint: number;

  private submenuDesktopBreakpoint: number;

  private windowSize: WindowSize;

  private smoothScroll: typeof SmoothScroll;

  $header: JQuery<HTMLElement>;

  $body: JQuery<HTMLElement>;

  $panelToggles: JQuery<HTMLElement>;

  $panels: JQuery<HTMLElement>;

  $subMenuButtons: JQuery<HTMLElement>;

  $subMenuLinks: JQuery<HTMLElement>;

  $subMenuItems: JQuery<HTMLElement>;

  $menuKeyboardTargets: JQuery<HTMLElement>;

  $subMenus: JQuery<HTMLElement>;

  $memberBar: JQuery<HTMLElement>;

  headerIsOpened: boolean;

  isScrollingDisabled: boolean;

  threshold: number;

  constructor() {
    this.$header = $('[data-header]');
    this.$body = $('body');
    this.$panelToggles = this.$header.find('[data-header-toggle]');
    this.$panels = this.$header.find('[data-header-panel]');
    this.$subMenuButtons = this.$header.find('[data-submenu-toggle]');
    this.$subMenuLinks = this.$header.find(
      '.menu-item-has-children > [data-submenu-trigger] > a',
    );
    this.$subMenuItems = this.$header.find('.menu-item-has-children');
    this.$menuKeyboardTargets = this.$header.find(
      'nav a, nav [data-submenu-toggle]',
    );
    this.$subMenus = this.$header.find('.sub-menu');
    this.$memberBar = this.$header.find('[data-header-member-bar]');

    this.headerIsOpened = false;
    this.isScrollingDisabled = false;
    this.threshold = 100;
    this.desktopBreakpoint = 1024;
    this.submenuDesktopBreakpoint = 768;

    this.windowSize = WindowSize.getInstance();
    this.smoothScroll = SmoothScroll;

    if (!this.$header.length) {
      return;
    }

    this.init();
  }

  init() {
    this.$panelToggles.on('click', this.onPanelToggleClick.bind(this));
    this.$subMenuButtons.on('click', this.onSubmenuToggleClick.bind(this));
    this.$subMenuLinks.on('click', this.onSubmenuLinkClick.bind(this));
    this.$subMenuItems.on('mouseenter', this.onSubmenuMouseEnter.bind(this));
    this.$subMenuItems.on('mouseleave', this.onSubmenuMouseLeave.bind(this));
    this.$subMenuItems.on('focusin', this.onSubmenuFocusIn.bind(this));
    this.$subMenuItems.on('focusout', this.onSubmenuFocusOut.bind(this));
    this.$menuKeyboardTargets.on('keydown', this.onMenuKeyDown.bind(this));
    this.$header.on('keydown', this.onKeyDown.bind(this));
    this.windowSize.addListener(this.onResize.bind(this));
    this.smoothScroll.scroll.on('scroll', this.onScroll.bind(this));

    this.syncAllPanelsAccessibility();
    this.syncAllSubmenusAccessibility();
    this.syncHeaderScrollState();

    if (!this.usesDesktopSubmenuLayout()) {
      this.$subMenus.hide();
    }
  }

  onPanelToggleClick(e: JQuery.ClickEvent) {
    const panelName = String($(e.currentTarget).data('header-toggle') || '');

    if (!panelName) {
      return;
    }

    if (panelName === 'main' && this.hasOpenPanels()) {
      this.closeAllPanels();
      return;
    }

    if (panelName === 'hub' && this.isPanelOpen(panelName)) {
      return;
    }

    if (this.isPanelOpen(panelName)) {
      this.closePanel(panelName);
      return;
    }

    this.openPanel(panelName);
  }

  onSubmenuToggleClick(e: JQuery.ClickEvent) {
    const $button = $(e.currentTarget);
    const $menuItem = $button.closest('.menu-item-has-children');
    this.toggleSubmenu($menuItem);
  }

  onSubmenuLinkClick(e: JQuery.ClickEvent) {
    const $link = $(e.currentTarget);
    const $menuItem = $link.closest('.menu-item-has-children');

    if (!$menuItem.length) {
      return;
    }

    e.preventDefault();
    this.toggleSubmenu($menuItem);
  }

  onSubmenuMouseEnter(e: JQuery.MouseEnterEvent) {
    if (!this.shouldOpenSubmenuOnHover()) {
      return;
    }

    this.openSubmenu($(e.currentTarget));
  }

  onSubmenuMouseLeave(e: JQuery.MouseLeaveEvent) {
    if (!this.shouldOpenSubmenuOnHover()) {
      return;
    }

    this.closeSubmenu($(e.currentTarget));
  }

  onSubmenuFocusIn(e: JQuery.FocusInEvent) {
    if (!this.usesDesktopSubmenuLayout()) {
      return;
    }

    this.openSubmenu($(e.currentTarget));
  }

  onSubmenuFocusOut(e: JQuery.FocusOutEvent) {
    if (!this.usesDesktopSubmenuLayout()) {
      return;
    }

    const $menuItem = $(e.currentTarget);

    window.setTimeout(() => {
      if ($menuItem.find(':focus').length) {
        return;
      }

      if (this.shouldOpenSubmenuOnHover() && $menuItem.is(':hover')) {
        return;
      }

      this.closeSubmenu($menuItem);
    }, 0);
  }

  onMenuKeyDown(e: JQuery.KeyDownEvent) {
    if (!this.usesDesktopSubmenuLayout()) {
      return;
    }

    const { key } = e;

    if (
      ![
        'ArrowUp',
        'ArrowRight',
        'ArrowLeft',
        'ArrowDown',
        'Escape',
      ].includes(key)
    ) {
      return;
    }

    const $currentTarget = $(e.currentTarget);
    const $parentLi = $currentTarget.closest('li');
    const $parentList = $parentLi.parent('ul');
    const $submenu = $parentLi.children('.sub-menu');
    const isSubMenu = $parentList.hasClass('sub-menu');
    const hasSubMenu = $submenu.length > 0;

    switch (key) {
      case 'Escape': {
        e.preventDefault();
        e.stopPropagation();

        if (isSubMenu) {
          const $parentItem = $parentList.parent('li');
          this.closeSubmenu($parentItem);
          this.getMenuItemPrimaryLink($parentItem).trigger('focus');
          return;
        }

        this.closeAllSubmenus();
        return;
      }

      case 'ArrowLeft': {
        e.preventDefault();

        if (isSubMenu) {
          const $parentItem = $parentList.parent('li');
          this.closeSubmenu($parentItem);
          this.getMenuItemPrimaryLink($parentItem).trigger('focus');
          return;
        }

        const $prevItem = $parentLi.prev('li');
        const $target = $prevItem.length
          ? this.getMenuItemPrimaryLink($prevItem)
          : this.getMenuItemPrimaryLink($parentList.children('li').last());

        $target.trigger('focus');
        return;
      }

      case 'ArrowRight': {
        e.preventDefault();

        if (isSubMenu) {
          const $parentItem = $parentList.parent('li');
          this.closeSubmenu($parentItem);
          this.getMenuItemPrimaryLink($parentItem).trigger('focus');
          return;
        }

        const $nextItem = $parentLi.next('li');
        const $target = $nextItem.length
          ? this.getMenuItemPrimaryLink($nextItem)
          : this.getMenuItemPrimaryLink($parentList.children('li').first());

        $target.trigger('focus');
        return;
      }

      case 'ArrowDown': {
        e.preventDefault();

        if (isSubMenu) {
          const $nextItem = $parentLi.next('li');
          const $target = $nextItem.length
            ? $nextItem.find('a, [data-submenu-toggle]').first()
            : $parentList.children('li').first().find('a, [data-submenu-toggle]').first();

          $target.trigger('focus');
          return;
        }

        if (hasSubMenu) {
          this.openSubmenu($parentLi);
          $submenu.find('a, [data-submenu-toggle]').first().trigger('focus');
        }
        return;
      }

      case 'ArrowUp': {
        e.preventDefault();

        if (isSubMenu) {
          const $prevItem = $parentLi.prev('li');
          const $target = $prevItem.length
            ? $prevItem.find('a, [data-submenu-toggle]').first()
            : $parentList.children('li').last().find('a, [data-submenu-toggle]').first();

          $target.trigger('focus');
          return;
        }

        if (hasSubMenu) {
          this.openSubmenu($parentLi);
          $submenu.find('a, [data-submenu-toggle]').last().trigger('focus');
        }
      }
    }
  }

  onKeyDown(e: JQuery.KeyDownEvent) {
    if (e.key !== 'Escape') {
      return;
    }

    this.closeAllSubmenus();
    this.closeAllPanels();
  }

  onResize() {
    if (this.isDesktop()) {
      this.closeAllPanels();
    }

    this.closeAllSubmenus();

    if (this.usesDesktopSubmenuLayout()) {
      this.$subMenus.stop(true, true).show();
      this.syncAllSubmenusAccessibility();
      this.syncAllPanelsAccessibility();
      return;
    }

    this.$subMenus.stop(true, true).hide();
    this.syncAllSubmenusAccessibility();
    this.syncAllPanelsAccessibility();
  }

  onScroll(e: Lenis) {
    if (!this.$header.length || e.isStopped) {
      return;
    }

    this.syncHeaderScrollState(e.scroll, e.direction);
  }

  openPanel(panelName: string) {
    this.getPanelNames().forEach((currentPanel) => {
      this.setPanelState(currentPanel, currentPanel === panelName);
    });

    this.headerIsOpened = true;
    this.$header.addClass('is-opened');
    this.$header.addClass(`is-${panelName}-opened`);
    this.syncHeaderScrollState();
    this.updateScrollLock();
  }

  closePanel(panelName: string) {
    this.setPanelState(panelName, false);
    this.$header.removeClass(`is-${panelName}-opened`);
    this.headerIsOpened = this.hasOpenPanels();

    if (!this.headerIsOpened) {
      this.$header.removeClass('is-opened');
    }

    this.syncHeaderScrollState();
    this.updateScrollLock();
  }

  closeAllPanels() {
    this.getPanelNames().forEach((panelName) => {
      this.setPanelState(panelName, false);
    });

    this.$header.removeClass('is-opened is-main-opened is-hub-opened');
    this.headerIsOpened = false;
    this.syncHeaderScrollState();
    this.updateScrollLock();
  }

  openNav() {
    this.openPanel('main');
  }

  closeNav() {
    this.closeAllPanels();
  }

  isPanelOpen(panelName: string) {
    const $toggle = this.$panelToggles.filter(
      `[data-header-toggle="${panelName}"]`,
    );

    return $toggle.attr('aria-expanded') === 'true';
  }

  hasOpenPanels() {
    return this.getPanelNames().some((panelName) => this.isPanelOpen(panelName));
  }

  setPanelState(panelName: string, isOpen: boolean) {
    if (!panelName) {
      return;
    }

    const $panel = this.$panels.filter(`[data-header-panel="${panelName}"]`);
    const $toggle = this.$panelToggles.filter(
      `[data-header-toggle="${panelName}"]`,
    );

    this.syncPanelAccessibility($panel, $toggle, isOpen);

    if (!isOpen) {
      $panel.find('.menu-item-has-children.is-opened').each((_, menuItem) => {
        this.closeSubmenu($(menuItem));
      });
      this.$header.removeClass(`is-${panelName}-opened`);
    }
  }

  syncPanelAccessibility(
    $panel: JQuery<HTMLElement>,
    $toggle: JQuery<HTMLElement>,
    isOpen: boolean,
  ) {
    if (!$toggle.length) {
      return;
    }

    $toggle.attr('aria-expanded', isOpen ? 'true' : 'false');

    if (!$panel.length || this.isDesktop()) {
      $panel.removeAttr('aria-hidden');
      return;
    }

    $panel.attr('aria-hidden', isOpen ? 'false' : 'true');
  }

  syncAllPanelsAccessibility() {
    this.$panels.each((_, panel) => {
      const $panel = $(panel);
      const panelName = String($panel.data('header-panel') || '');
      const $toggle = this.$panelToggles.filter(
        `[data-header-toggle="${panelName}"]`,
      );
      const isOpen = $toggle.attr('aria-expanded') === 'true';

      this.syncPanelAccessibility($panel, $toggle, isOpen);
    });
  }

  syncSubmenuAccessibility(
    $submenu: JQuery<HTMLElement>,
    $button: JQuery<HTMLElement>,
    isOpen: boolean,
  ) {
    $button.attr('aria-expanded', isOpen ? 'true' : 'false');

    if (this.usesDesktopSubmenuLayout()) {
      $submenu.removeAttr('aria-hidden');
      return;
    }

    $submenu.attr('aria-hidden', isOpen ? 'false' : 'true');
  }

  syncAllSubmenusAccessibility() {
    this.$subMenuItems.each((_, menuItem) => {
      const $menuItem = $(menuItem);
      const $button = $menuItem
        .children('[data-submenu-trigger]')
        .children('[data-submenu-toggle]');
      const $submenu = $menuItem.children('.sub-menu');
      const isOpen = $button.attr('aria-expanded') === 'true';

      if ($button.length && $submenu.length) {
        this.syncSubmenuAccessibility($submenu, $button, isOpen);
      }
    });
  }

  getPanelNames() {
    const panelNames = new Set<string>();

    this.$panelToggles.each((_, toggle) => {
      const panelName = String($(toggle).data('header-toggle') || '');

      if (panelName) {
        panelNames.add(panelName);
      }
    });

    this.$panels.each((_, panel) => {
      const panelName = String($(panel).data('header-panel') || '');

      if (panelName) {
        panelNames.add(panelName);
      }
    });

    return Array.from(panelNames);
  }

  isDesktop() {
    return this.windowSize.getSize().width >= this.desktopBreakpoint;
  }

  usesDesktopSubmenuLayout() {
    return this.windowSize.getSize().width >= this.submenuDesktopBreakpoint;
  }

  supportsHover() {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  shouldOpenSubmenuOnHover() {
    return this.usesDesktopSubmenuLayout() && this.supportsHover();
  }

  updateScrollLock() {
    const shouldLock = this.hasOpenPanels();

    if (shouldLock && !this.isScrollingDisabled) {
      this.smoothScroll.disableScrolling();
      this.isScrollingDisabled = true;
      return;
    }

    if (!shouldLock && this.isScrollingDisabled) {
      this.smoothScroll.enableScrolling();
      this.isScrollingDisabled = false;
    }
  }

  hasMemberBar() {
    return this.$memberBar.length > 0;
  }

  getCurrentScrollPosition() {
    return this.smoothScroll.scroll?.scroll ?? window.scrollY ?? 0;
  }

  syncHeaderScrollState(scroll = this.getCurrentScrollPosition(), direction = 0) {
    const shouldBeSolid = scroll > this.threshold || this.hasMemberBar();
    const isScrollingUp = direction < 0 && scroll > this.threshold;
    const isScrollingDown = direction > 0 && scroll > this.threshold;
    const wasScrollingUp = this.$header.hasClass('is-scrolling-up');
    const wasScrollingDown = this.$header.hasClass('is-scrolling-down');

    this.$header.toggleClass('is-solid', shouldBeSolid);
    this.$header.toggleClass('is-scrolling-up', isScrollingUp);
    this.$header.toggleClass('is-scrolling-down', isScrollingDown);

    if (isScrollingUp !== wasScrollingUp || isScrollingDown !== wasScrollingDown) {
      this.$header.get(0)?.dispatchEvent(
        new CustomEvent('header:scroll-state-changed', {
          detail: { isScrollingDown, isScrollingUp },
        }),
      );
    }
  }

  openSubmenu($menuItem: JQuery<HTMLElement>) {
    const $button = $menuItem
      .children('[data-submenu-trigger]')
      .children('[data-submenu-toggle]');
    const $submenu = $menuItem.children('.sub-menu');

    if (!$button.length || !$submenu.length) {
      return;
    }

    $menuItem
      .siblings('.menu-item-has-children.is-opened')
      .each((_, sibling) => this.closeSubmenu($(sibling)));

    if ($button.attr('aria-expanded') === 'true') {
      return;
    }

    $menuItem.addClass('is-opened');
    this.syncSubmenuAccessibility($submenu, $button, true);

    if (!this.usesDesktopSubmenuLayout()) {
      $submenu.stop(true, true).slideDown(300);
      return;
    }

    $submenu.stop(true, true).show();
  }

  closeSubmenu($menuItem: JQuery<HTMLElement>) {
    const $button = $menuItem
      .children('[data-submenu-trigger]')
      .children('[data-submenu-toggle]');
    const $submenu = $menuItem.children('.sub-menu');

    if (!$button.length || !$submenu.length) {
      return;
    }

    if ($button.attr('aria-expanded') !== 'true') {
      return;
    }

    $submenu.find('.menu-item-has-children.is-opened').each((_, childMenuItem) => {
      this.closeSubmenu($(childMenuItem));
    });

    $menuItem.removeClass('is-opened');
    this.syncSubmenuAccessibility($submenu, $button, false);

    if (!this.usesDesktopSubmenuLayout()) {
      $submenu.stop(true, true).slideUp(300);
      return;
    }

    $submenu.stop(true, true).show();
  }

  closeAllSubmenus() {
    this.$header.find('.menu-item-has-children.is-opened').each((_, menuItem) => {
      this.closeSubmenu($(menuItem));
    });
  }

  toggleSubmenu($menuItem: JQuery<HTMLElement>) {
    const $submenu = $menuItem.children('.sub-menu');
    const $button = $menuItem
      .children('[data-submenu-trigger]')
      .children('[data-submenu-toggle]');

    if (!$menuItem.length || !$submenu.length || !$button.length) {
      return;
    }

    const isOpen = $button.attr('aria-expanded') === 'true';

    if (isOpen) {
      this.closeSubmenu($menuItem);
      return;
    }

    $menuItem
      .siblings('.menu-item-has-children.is-opened')
      .each((_, sibling) => this.closeSubmenu($(sibling)));

    this.openSubmenu($menuItem);
  }

  getMenuItemPrimaryLink($menuItem: JQuery<HTMLElement>) {
    const $link = $menuItem
      .children('[data-submenu-trigger]')
      .children('a')
      .first();

    if ($link.length) {
      return $link;
    }

    return $menuItem.children('a').first();
  }
}

export default Header;
